/**
 * LA CARTE: deux iles, un seul repere, et de la mer entre elles.
 *
 * Le deuxieme monde aurait pu etre une autre carte, avec son propre systeme de
 * coordonnees et un voyage qui teleporte d'un espace a l'autre. Une seule
 * phrase du proprietaire l'a interdit: « techniquement, tu pourrais y aller a
 * la nage ou en barque ». Nager d'un monde a l'autre n'a de sens que si les
 * deux sont dans LE MEME plan x/z — et le jour ou ils y sont, il n'y a plus
 * rien a ecrire: le pas partage, l'autorite du serveur sur les positions, la
 * presence des autres joueurs, les vehicules, la nage, le filet anti-blocage
 * n'ont jamais su ce qu'est un « monde ». Ils ne connaissent que des
 * coordonnees, et des coordonnees, il y en a maintenant plus loin.
 *
 * Cette classe est donc un AIGUILLAGE, pas une simulation: elle repond a
 * `ITerrain` en demandant a l'ile qui contient le point, et parle d'ocean
 * partout ailleurs.
 *
 * DEUX POINTS OU IL AURAIT ETE FACILE DE SE TROMPER:
 *
 *  1. **Hors des iles, c'est de l'EAU.** `Terrain.hauteur` rendait `SEA_LEVEL`
 *     hors du monde, et `estEau` compare avec un `<` STRICT: la haute mer
 *     aurait donc ete de la terre ferme — on aurait marche jusqu'au monde 2 au
 *     lieu d'y nager, et la grille de navigation y aurait trace des chemins.
 *
 *  2. **Le fond descend PROGRESSIVEMENT.** Il part de la hauteur reelle du bord
 *     de l'ile la plus proche et rejoint la profondeur du large sur
 *     `PORTEE_PLONGEE`. Sans cela, le fond marin ferait une marche de douze
 *     metres tout autour de chaque ile — c'est exactement la « longue arete
 *     rectiligne » que le rendu de l'eau a deja eu a effacer une fois, et la
 *     raison pour laquelle c'est ICI que le calcul vit desormais: deux regles
 *     pour la meme question finissent toujours par diverger.
 */
import {
  CARTE_X,
  CARTE_Z,
  ILE2_COTE,
  ILE2_X0,
  ILE2_Z0,
  PROFONDEUR_LARGE,
  SEA_LEVEL,
  WORLD_SIZE,
} from '../constants.ts'
import type { BiomeId, ITerrain, TerrainSample } from '../types/world.ts'
import type { RegionDef } from '../types/ile2.ts'
import { clamp, lerp, smoothstep } from './alea.ts'
import { Ile2 } from './ile2.ts'
import { Terrain, type Chute } from './terrain.ts'

/** Distance, hors d'une ile, sur laquelle on rejoint la profondeur du large. */
export const PORTEE_PLONGEE = 165

/**
 * Une ile de la carte: un `ITerrain` qui sait ou il est pose et qui sait se
 * dessiner. `bornesChunk` et `normale` servent au rendu du terrain,
 * `couleurSol` a sa teinte — chaque ile repond pour elle-meme, ce qui est la
 * seule facon d'avoir une ile de sable a l'est sans que la disposition des
 * biomes de la premiere ile ne la peigne en roche.
 */
export interface Ile extends ITerrain {
  /** Coin nord-ouest et cote de l'emprise carree, en coordonnees du monde. */
  readonly x0: number
  readonly z0: number
  readonly cote: number
  /** La chute d'eau de cette ile, s'il y en a une. */
  readonly chute: Chute | null
  contient(x: number, z: number): boolean
  normale(x: number, z: number, sortie: [number, number, number]): void
  /** Hauteur minimale et maximale sur un rectangle du monde. */
  bornes(x0: number, x1: number, z0: number, z1: number): { min: number; max: number }
  bornesChunk(cx: number, cz: number): { min: number; max: number }
  couleurSol(x: number, z: number): number
}

export class Carte implements ITerrain {
  readonly seed: number
  /** Les iles, dans l'ordre: la premiere est le monde d'origine. */
  readonly iles: readonly Ile[]
  readonly ile1: Terrain
  readonly ile2: Ile2

  constructor(seed: number) {
    this.seed = seed | 0
    this.ile1 = new Terrain(seed)
    this.ile2 = new Ile2(seed)
    this.iles = [this.ile1, this.ile2]
  }

  /**
   * La chute d'eau de l'ile 1.
   *
   * Elle GARDE son nom au singulier, et ce n'est pas de la paresse: tout ce qui
   * la lit — le rendu du sol de l'ile 1, un test de bout en bout, le test
   * d'identite du monde 1 — parle de CELLE-LA. Ce qui veut toutes les chutes du
   * monde demande `chutes`.
   */
  get chute(): Chute | null {
    return this.ile1.chute
  }

  /** Toutes les chutes du monde: l'ile 1 a la sienne, l'ile 2 la sienne. */
  get chutes(): readonly Chute[] {
    const sortie: Chute[] = []
    for (const ile of this.iles) {
      if (ile.chute) sortie.push(ile.chute)
    }
    return sortie
  }

  /**
   * La region de l'ile 2 sous ce point, ou null ailleurs.
   *
   * C'est par ici que le semis des ressources demande « qu'est-ce qui pousse
   * ici ? »: onze regions ont onze reponses, et huit biomes ne suffiraient pas
   * a les distinguer.
   */
  regionAuPoint(x: number, z: number): RegionDef | null {
    return this.ile2.regionAuPoint(x, z)
  }

  /** La vitesse de marche du lieu, quand une region en impose une. */
  vitesseDuLieu(x: number, z: number): number | undefined {
    return this.ile2.regionAuPoint(x, z)?.vitesse
  }

  // ---------------------------------------------------------------- aiguillage

  /** L'ile qui contient ce point, ou null si l'on est en mer. */
  ileDe(x: number, z: number): Ile | null {
    for (const ile of this.iles) {
      if (ile.contient(x, z)) return ile
    }
    return null
  }

  /**
   * L'ile la plus proche, la distance a son emprise et le point de son bord le
   * plus proche. En mer, c'est de ce bord-la que part le fond — et c'est aussi
   * ce dont le rendu de l'eau a besoin pour raccorder son plateau continental
   * au rivage sans montrer de marche.
   */
  bordProche(x: number, z: number): { ile: Ile; d: number; bx: number; bz: number } {
    let meilleure = this.iles[0] as Ile
    let meilleureD = Infinity
    let bx = x
    let bz = z
    for (const ile of this.iles) {
      const cx = clamp(x, ile.x0, ile.x0 + ile.cote)
      const cz = clamp(z, ile.z0, ile.z0 + ile.cote)
      const d = Math.hypot(x - cx, z - cz)
      if (d < meilleureD) {
        meilleureD = d
        meilleure = ile
        bx = cx
        bz = cz
      }
    }
    return { ile: meilleure, d: meilleureD, bx, bz }
  }

  // ---------------------------------------------------------------- ITerrain

  hauteur(x: number, z: number): number {
    const ile = this.ileDe(x, z)
    if (ile) return ile.hauteur(x, z)
    const p = this.bordProche(x, z)
    const bord = p.ile.hauteur(p.bx, p.bz)
    return lerp(bord, SEA_LEVEL - PROFONDEUR_LARGE, smoothstep(0, PORTEE_PLONGEE, p.d))
  }

  biome(x: number, z: number): BiomeId {
    const ile = this.ileDe(x, z)
    if (ile) return ile.biome(x, z)
    const p = this.bordProche(x, z)
    return p.ile.biome(p.bx, p.bz)
  }

  pente(x: number, z: number): number {
    const ile = this.ileDe(x, z)
    if (ile) return ile.pente(x, z)
    const d = 1
    const gx = (this.hauteur(x + d, z) - this.hauteur(x - d, z)) / (2 * d)
    const gz = (this.hauteur(x, z + d) - this.hauteur(x, z - d)) / (2 * d)
    return Math.hypot(gx, gz)
  }

  estEau(x: number, z: number): boolean {
    return this.hauteur(x, z) < SEA_LEVEL
  }

  echantillon(x: number, z: number): TerrainSample {
    const ile = this.ileDe(x, z)
    if (ile) return ile.echantillon(x, z)
    return { hauteur: this.hauteur(x, z), biome: this.biome(x, z), pente: this.pente(x, z), sol: 'eau' }
  }

  // ------------------------------------------------------------------ rendu

  normale(x: number, z: number, sortie: [number, number, number]): void {
    const ile = this.ileDe(x, z)
    if (ile) {
      ile.normale(x, z, sortie)
      return
    }
    sortie[0] = 0
    sortie[1] = 1
    sortie[2] = 0
  }

  /** La teinte du sol: celle de l'ile, ou celle du bord le plus proche en mer. */
  couleurSol(x: number, z: number): number {
    const ile = this.ileDe(x, z)
    if (ile) return ile.couleurSol(x, z)
    const p = this.bordProche(x, z)
    return p.ile.couleurSol(p.bx, p.bz)
  }

  /** Distance hors de toute ile, en metres. Zero sur la terre et ses hauts-fonds. */
  dehors(x: number, z: number): number {
    if (this.ileDe(x, z)) return 0
    return this.bordProche(x, z).d
  }
}

/**
 * Marge sous laquelle une maille compte comme SECHE, en metres.
 *
 * La houle monte de quelques dizaines de centimetres et l'interpolation de la
 * grille joue un peu: trop serre, on decouvre un trou dans la mer au bord d'un
 * ruisseau; trop large, on paie quelques mailles pour rien. Le mauvais cote de
 * l'erreur n'est pas le meme des deux cotes.
 */
export const MARGE_SEC = 1.0

/**
 * Vrai si cette maille est entierement au SEC, a l'interieur d'une ile.
 *
 * C'est un MINIMUM EXACT sur la grille au metre, jamais un test des quatre
 * coins: la lecon de la piste d'aeroport, « un sommet ne dit la hauteur du sol
 * qu'EN UN POINT ». Un ruisseau de trois metres au travers d'une maille de
 * quatre passerait entre les coins, et la mer aurait un trou.
 */
export function mailleSeche(carte: Carte, x0: number, x1: number, z0: number, z1: number): boolean {
  for (const ile of carte.iles) {
    if (x0 < ile.x0 || x1 > ile.x0 + ile.cote) continue
    if (z0 < ile.z0 || z1 > ile.z0 + ile.cote) continue
    return ile.bornes(x0, x1, z0, z1).min > SEA_LEVEL + MARGE_SEC
  }
  return false
}

/**
 * Vrai si le point est dans les bornes de la CARTE.
 * Le monde 1 occupait [0, WORLD_SIZE]²; la carte va maintenant plus loin sur x.
 */
export function dansLaCarte(x: number, z: number): boolean {
  return x >= 0 && z >= 0 && x <= CARTE_X && z <= CARTE_Z
}

/** Vrai si le point est sur l'ile d'origine. */
export function dansLeMonde1(x: number, z: number): boolean {
  return x >= 0 && z >= 0 && x <= WORLD_SIZE && z <= WORLD_SIZE
}

/** Vrai si le point est sur l'ile 2. */
export function dansLeMonde2(x: number, z: number): boolean {
  return x >= ILE2_X0 && z >= ILE2_Z0 && x <= ILE2_X0 + ILE2_COTE && z <= ILE2_Z0 + ILE2_COTE
}

/**
 * DANS QUEL MONDE EST CE POINT: 1 pour l'ile d'origine, 2 pour l'autre.
 *
 * En mer, c'est l'ile la PLUS PROCHE qui repond. Il faut bien repondre quelque
 * chose — un nageur au large est quelque part — et « la plus proche » est la
 * seule reponse qu'un joueur puisse lire sans explication. Sur la terre ferme,
 * c'est-a-dire pour tout ce qui compte vraiment (un batiment, un aeroport, un
 * vehicule gare), la question est exacte: le point est dans une emprise ou dans
 * l'autre.
 *
 * Fonction PURE de la geometrie de la carte: elle n'a pas besoin d'une `Carte`
 * construite, donc le reseau et les batiments peuvent l'appeler sans trainer le
 * generateur de terrain avec eux.
 */
export function numeroDeMonde(x: number, z: number): 1 | 2 {
  if (dansLeMonde2(x, z)) return 2
  if (dansLeMonde1(x, z)) return 1
  const d1 = Math.hypot(x - clamp(x, 0, WORLD_SIZE), z - clamp(z, 0, WORLD_SIZE))
  const d2 = Math.hypot(
    x - clamp(x, ILE2_X0, ILE2_X0 + ILE2_COTE),
    z - clamp(z, ILE2_Z0, ILE2_Z0 + ILE2_COTE),
  )
  return d2 < d1 ? 2 : 1
}
